import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * ContactMethods — 3 ways to reach us, presented as tall image cards
 * where each card has a hover-lift + primary dot accent.
 *
 * Each card is clickable (email / phone / whatsapp) — the whole card is
 * an <a> so clicking anywhere on it takes action, which matters on mobile.
 */

interface Method {
  index: string;
  title: string;
  description: string;
  meta: string;
  icon: string;
  href: string;
  image: string;
  ctaLabel: string;
}

const METHODS: Method[] = [
  {
    index: "01",
    title: "Write to us",
    description:
      "The quietest way in. Take your time — we usually reply within a day.",
    meta: "hello@serene.studio",
    icon: "ri-mail-line",
    href: "mailto:hello@serene.studio",
    ctaLabel: "Open Email",
    image:
      "https://images.pexels.com/photos/6157228/pexels-photo-6157228.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1500&fit=crop",
  },
  {
    index: "02",
    title: "Call the studio",
    description:
      "Ten minutes on the phone to introduce yourself and hear a real voice.",
    meta: "+351 210 000 000",
    icon: "ri-phone-line",
    href: "tel:+351210000000",
    ctaLabel: "Call Now",
    image:
      "https://images.pexels.com/photos/6157050/pexels-photo-6157050.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1500&fit=crop",
  },
  {
    index: "03",
    title: "Message us",
    description:
      "A quicker line for small questions and rescheduling. WhatsApp works.",
    meta: "+351 910 000 000",
    icon: "ri-whatsapp-line",
    href: "https://wa.me/351910000000",
    ctaLabel: "Open WhatsApp",
    image:
      "https://images.pexels.com/photos/887751/pexels-photo-887751.jpeg?auto=compress&cs=tinysrgb&w=1200&h=1500&fit=crop",
  },
];

export default function ContactMethods() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const gridRef = useEnterAnimation<HTMLDivElement>({ delay: 300 });

  return (
    <section
      id="contacts-methods"
      className="relative w-full bg-background-100"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            Contacts / 02
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 02 · Three ways in
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex flex-col items-center justify-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto">
          <div ref={eyebrowRef} className="mb-8 flex items-center gap-3">
            <span
              className="font-heading"
              style={{
                fontSize: "clamp(2.75rem, 4vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.03em",
                color: "oklch(var(--foreground-950) / 0.35)",
                lineHeight: 1,
                fontVariationSettings: "'opsz' 144",
              }}
            >
              02
            </span>
            <span className="w-12 h-[1px] bg-foreground-950/25"></span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
              How to reach us
            </span>
          </div>

          <h2
            ref={titleRef}
            className="mb-16 max-w-[900px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.5rem, 5vw, 5rem)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              lineHeight: 1.05,
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            Three ways to say
            <br />
            <span className="italic text-foreground-950/85">
              a first hello.
            </span>
          </h2>

          <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
            {METHODS.map((m) => (
              <a
                key={m.index}
                href={m.href}
                target={m.href.startsWith("http") ? "_blank" : undefined}
                rel={m.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="group relative w-full overflow-hidden rounded-2xl cursor-pointer transition-transform duration-500 hover:-translate-y-1"
                style={{ aspectRatio: "3 / 4" }}
              >
                <img
                  src={m.image}
                  alt={m.title}
                  title={m.title}
                  className="absolute inset-0 w-full h-full object-cover object-center select-none transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                  draggable={false}
                />

                {/* Bottom-up gradient */}
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0"
                  style={{
                    height: "62%",
                    background:
                      "linear-gradient(to top, rgba(20,15,10,0.78) 0%, rgba(20,15,10,0.42) 45%, rgba(20,15,10,0) 100%)",
                  }}
                />

                {/* Top-left index */}
                <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                  <span
                    className="uppercase font-medium"
                    style={{
                      fontSize: "10px",
                      letterSpacing: "0.3em",
                      color: "rgba(255,255,255,0.72)",
                    }}
                  >
                    Method · {m.index}
                  </span>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50/25 backdrop-blur-md text-white">
                    <i className={`${m.icon} text-base`}></i>
                  </span>
                </div>

                {/* Bottom copy */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                  <h3
                    className="mb-2"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(1.5rem, 2vw, 2rem)",
                      fontWeight: 400,
                      letterSpacing: "-0.015em",
                      lineHeight: 1.05,
                      color: "#FFFFFF",
                    }}
                  >
                    {m.title}
                  </h3>
                  <p
                    className="italic mb-3 max-w-[300px]"
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "clamp(0.9rem, 1vw, 1.05rem)",
                      fontWeight: 300,
                      lineHeight: 1.5,
                      color: "rgba(255,255,255,0.82)",
                    }}
                  >
                    {m.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                      <span
                        className="uppercase font-medium"
                        style={{
                          fontSize: "10px",
                          letterSpacing: "0.3em",
                          color: "rgba(255,255,255,0.75)",
                        }}
                      >
                        {m.meta}
                      </span>
                    </div>
                    <span
                      className="inline-flex items-center gap-1.5 transition-transform duration-300 group-hover:translate-x-1"
                      style={{
                        fontSize: "10px",
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 500,
                      }}
                    >
                      {m.ctaLabel}
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}